import { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, Plus, Edit3, Trash2, Search, Shield, FileText, ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TABS = [
  { key: "doc-types", label: "Document Types", icon: FileText },
  { key: "audit-log", label: "Audit Log", icon: ListChecks },
  { key: "roles", label: "Role Management", icon: Shield },
];

export default function SMSAdmin() {
  const [activeTab, setActiveTab] = useState("doc-types");

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 tracking-wide">
          SMS Admin
        </h1>
        <p className="text-xs text-slate-500">Configure document types, view audit logs, manage roles</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        {TABS.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50/50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "doc-types" && <DocumentTypesTab />}
      {activeTab === "audit-log" && <AuditLogTab />}
      {activeTab === "roles" && <RolesTab />}
    </div>
  );
}

/* ─── Document Types Tab ─── */
function DocumentTypesTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", workflow_stages: "" });

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${API}/sms/document-types`, {
        headers: getHeaders(), withCredentials: true,
      });
      setTypes(res.data.document_types || []);
    } catch { toast.error("Failed to load document types"); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ code: "", name: "", workflow_stages: "recommend,qc,approve" });
    setShowDialog(true);
  };

  const openEdit = (dt) => {
    setEditing(dt);
    setForm({
      code: dt.code,
      name: dt.name,
      workflow_stages: (dt.workflow_stages || []).join(","),
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Code and name are required");
      return;
    }
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        workflow_stages: form.workflow_stages.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (editing) {
        await axios.put(`${API}/sms/document-types/${editing.id}`, payload, {
          headers: getHeaders(), withCredentials: true,
        });
        toast.success("Document type updated");
      } else {
        await axios.post(`${API}/sms/document-types`, payload, {
          headers: getHeaders(), withCredentials: true,
        });
        toast.success("Document type created");
      }
      setShowDialog(false);
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDeactivate = async (dt) => {
    if (!confirm(`Deactivate "${dt.code} — ${dt.name}"?`)) return;
    try {
      await axios.delete(`${API}/sms/document-types/${dt.id}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Document type deactivated");
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">{types.filter(t => t.is_active !== false).length} active types</p>
        <Button size="sm" className="h-7 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500"
          onClick={openCreate}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New Type
        </Button>
      </div>
      <Card className="bg-white border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-app border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Code</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Name</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Workflow Stages</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Status</th>
              <th className="text-center px-3 py-2 font-medium text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {types.map(dt => (
              <tr key={dt.id} className="hover:bg-app">
                <td className="px-3 py-2 font-mono text-slate-700">{dt.code}</td>
                <td className="px-3 py-2 text-slate-800">{dt.name}</td>
                <td className="px-3 py-2 text-slate-500">
                  {(dt.workflow_stages || []).join(" → ")}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 font-medium ${
                    dt.is_active !== false
                      ? 'bg-green-100 text-green-600'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {dt.is_active !== false ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                      onClick={() => openEdit(dt)}
                    >
                      <Edit3 className="w-3 h-3 text-slate-500" />
                    </Button>
                    {dt.is_active !== false && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                        onClick={() => handleDeactivate(dt)}
                      >
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              {editing ? "Edit Document Type" : "New Document Type"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">Code *</Label>
              <Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
                placeholder="e.g. KK11" maxLength={6}
                className="rounded-none border-slate-300 h-9 text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Submission" maxLength={100}
                className="rounded-none border-slate-300 h-9 text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">Workflow Stages (comma-separated)</Label>
              <Input value={form.workflow_stages}
                onChange={e => setForm(p => ({ ...p, workflow_stages: e.target.value }))}
                placeholder="recommend,qc,approve"
                className="rounded-none border-slate-300 h-9 text-xs"
              />
              <p className="text-[10px] text-slate-400">Default: recommend,qc,approve</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-none text-xs"
              onClick={() => setShowDialog(false)}
            >Cancel</Button>
            <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-xs"
              onClick={handleSave}
            >{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Audit Log Tab ─── */
function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/sms/admin/audit-log`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLogs(res.data.actions || []);
    } catch { toast.error("Failed to load audit log"); }
    setLoading(false);
  };

  const filtered = search
    ? logs.filter(l =>
        (l.doc_ref || "").toLowerCase().includes(search.toLowerCase()) ||
        (l.actor_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (l.action || "").toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by doc ref, actor, action..." className="pl-8 h-8 text-xs rounded-none border-slate-300"
          />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} entries</span>
      </div>
      <Card className="bg-white border-slate-200 overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-app">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Date</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Document</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Action</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Actor</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Comment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No audit entries found</td></tr>
            ) : filtered.map(log => (
              <tr key={log.id} className="hover:bg-app">
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                  {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 font-mono text-slate-700">{log.doc_ref || "—"}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 uppercase">
                    {log.action}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-700">{log.actor_name || "—"}</td>
                <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate">{log.comment || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ─── Role Management Tab ─── */
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ position_id: "", role: "" });

  const ROLE_OPTIONS = [
    { value: "admin", label: "SMS Admin" },
  ];

  useEffect(() => {
    Promise.all([fetchRoles(), fetchPositions()]);
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/sms/admin/roles`, {
        headers: getHeaders(), withCredentials: true,
      });
      setRoles(res.data.roles || []);
    } catch { toast.error("Failed to load roles"); }
    setLoading(false);
  };

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API}/sms/positions`, {
        headers: getHeaders(), withCredentials: true,
      });
      setPositions(res.data.positions || []);
    } catch {}
  };

  const handleAdd = async () => {
    if (!form.position_id || !form.role) {
      toast.error("Position and role are required");
      return;
    }
    try {
      await axios.post(`${API}/sms/admin/roles`, form, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Role assigned");
      setShowDialog(false);
      setForm({ position_id: "", role: "" });
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign role");
    }
  };

  const handleRemove = async (roleId) => {
    if (!confirm("Remove this role assignment?")) return;
    try {
      await axios.delete(`${API}/sms/admin/roles/${roleId}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Role removed");
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove role");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">{roles.length} role assignment{roles.length !== 1 ? 's' : ''}</p>
        <Button size="sm" className="h-7 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500"
          onClick={() => setShowDialog(true)}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Assign Role
        </Button>
      </div>
      <Card className="bg-white border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-app border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Position</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Post #</th>
              <th className="text-left px-3 py-2 font-medium text-slate-500 uppercase">Role</th>
              <th className="text-center px-3 py-2 font-medium text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {roles.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">No role assignments yet</td></tr>
            ) : roles.map(r => (
              <tr key={r.id} className="hover:bg-app">
                <td className="px-3 py-2 text-slate-800">{r.position_title || "—"}</td>
                <td className="px-3 py-2 text-slate-500 font-mono">{r.post_number || "—"}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-600 uppercase font-medium">
                    {r.role}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                    onClick={() => handleRemove(r.id)}
                  >
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Assign Role Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-white rounded-none max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">Assign SMS Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">Position *</Label>
              <Select value={form.position_id} onValueChange={v => setForm(p => ({ ...p, position_id: v }))}>
                <SelectTrigger className="rounded-none border-slate-300 h-9 text-xs bg-white">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  {positions.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">
                      {p.title} ({p.post_number})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">Role *</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="rounded-none border-slate-300 h-9 text-xs bg-white">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  {ROLE_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value} className="text-xs">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-none text-xs"
              onClick={() => setShowDialog(false)}
            >Cancel</Button>
            <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-xs"
              onClick={handleAdd}
            >Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
